import { Divider } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/ConfirmationModal';
import LinkButton from 'components/LinkButton';
import {
  type AtlasGqlMaintenanceActivity,
  AtlasGqlMaintenanceActivityStatus,
  useUpdateMaintenanceActivityMutation,
} from 'types/atlas-graphql';

import { HeaderWrapper, TextSpan } from './maintenanceActivities.style';

interface IMaintenanceActivityStatusHeader {
  maintenanceActivity: AtlasGqlMaintenanceActivity;
}

export const MaintenanceActivityStatusHeader: React.FunctionComponent<
  IMaintenanceActivityStatusHeader
> = ({ maintenanceActivity }) => {
  const { t } = useTranslation();
  const { id, name, status } = maintenanceActivity;
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const isEnded = status === AtlasGqlMaintenanceActivityStatus.Ended;

  const [updateMaintenanceActivityMutation] = useUpdateMaintenanceActivityMutation();

  const handleEnd = () => {
    updateMaintenanceActivityMutation({
      variables: {
        id: id,
        input: {
          status: AtlasGqlMaintenanceActivityStatus.Ended,
        },
      },
    });
    setIsConfirmOpen(false);
    setIsEditing(false);
  };

  const handleCancelEnd = () => {
    setIsConfirmOpen(false);
    setIsEditing(false);
  };

  const modalHeaderText = t('task.endRecurringTaskTitle', 'End Recurring Task.');
  const modalWarningText = (name: string) =>
    t(
      'task.endRecurrenceWarning',
      'End the recurrence of "{{name}}"? New Tasks will no longer be created from this Recurring Task Group once the current Active Task is completed.',
      { name }
    );

  if (!isEditing) {
    return (
      <HeaderWrapper>
        <TextSpan>
          {isEnded ? t('task.statusEnded', 'Ended') : t('task.statusActive', 'Active')}
        </TextSpan>
        {!isEnded && (
          <LinkButton data-testid="edit-button" onClick={() => setIsEditing(true)}>
            {t('common.edit', 'Edit')}
          </LinkButton>
        )}
      </HeaderWrapper>
    );
  }

  return (
    <>
      <LinkButton data-testid="edit-button" onClick={() => setIsEditing(false)}>
        {t('common.cancel', 'Cancel')}
      </LinkButton>
      <Divider type="vertical" />
      <LinkButton data-testid="edit-button" onClick={() => setIsConfirmOpen(true)} color={'red'}>
        {t('task.endRecurrence', 'End Recurrence')}
      </LinkButton>
      <ConfirmationModal
        open={isConfirmOpen}
        title={modalHeaderText}
        message={modalWarningText(name)}
        okText={t('common.okay', 'Okay')}
        cancelText={t('common.cancel', 'Cancel')}
        onOk={handleEnd}
        onCancel={handleCancelEnd}
      />
    </>
  );
};
